
import { defineComponent, ref, onMounted, onBeforeUnmount, nextTick, watch, computed } from 'vue'
import * as cornerstone from 'cornerstone-core'
import * as cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader'
import * as dicomParser from 'dicom-parser'
import { useViewerStore } from '@/store/viewer'
import { useDicomStore } from '@/store/dicom'
import { useReadingResultStore } from '@/store/readingResult'
import { usePopupStore } from '@/store/popup'
import { useUsersStore } from '@/store/users'
import { debounce } from 'lodash'
import viridisLUT from './../viridis_lut.json'
import * as tf from '@tensorflow/tfjs'
import { useExportStore } from '@/store/export'
import { usePacsStore } from '@/store/pacs'
import { useTimerStore } from '@/store/timer'
import moment from 'moment';

cornerstone.registerImageLoader('wadouri', cornerstoneWADOImageLoader.loadImage)
cornerstoneWADOImageLoader.external.cornerstone = cornerstone
cornerstoneWADOImageLoader.external.dicomParser = dicomParser
cornerstoneWADOImageLoader.configure({})

export default defineComponent({
  name: 'DicomViewer',

  setup() {
    const image = ref<any>(null)
    const dicomStore = useDicomStore();
    const readingResultStore = useReadingResultStore();
    const popupStore = usePopupStore();
    const usersStore = useUsersStore();

    const baseURL = "";
    const dicomImg = computed(() => dicomStore.getDicomImg)
    const buffers = computed(() => dicomStore.getBinFiles)
    const dicomElementLeft = ref<HTMLDivElement | null>(null)
    const dicomElementRight = ref<HTMLDivElement | null>(null)
    const maskElement = ref<HTMLDivElement | null>(null)
    const viewerElement = ref<HTMLDivElement | null>(null)
    const viewerWidth = ref(0)
    const windowWidth = ref<number | null>(null)
    const windowCenter = ref<number | null>(null)
    const tubeThreshold = ref<number>(0.5)
    const imageWeight = ref<number>(0.5)
    const initialWindowWidth = ref(0)
    const initialWindowCenter = ref(0)
    const tubeCanvas = ref<HTMLCanvasElement | null>(null)
    const heatmapCanvas = ref<HTMLCanvasElement | null>(null)
    const probCanvas = ref<HTMLCanvasElement | null>(null)
    let cachedCompleteHeatmapTensor: tf.Tensor3D | null = null
    let cachedIncompleteHeatmapTensor: tf.Tensor3D | null = null
    let cachedTubeTensor: tf.Tensor2D | null = null
    let resizeObserver: ResizeObserver | null = null;
    const dicomFile = computed(() => dicomStore.getTargetDicomFile);
    const dicomInfo = computed(() => dicomStore.getTargetDicomInfo);
    const selectHeatmap = ref('')
    const showTubeCanvas = computed(() => dicomStore.getMaskCheckState);
    const showHeatmapCanvas = computed(() => dicomStore.getHeatMapCheckState); 
    const showProbCanvas = computed(() => dicomStore.getProbabilityCheckState);
    const readingResultList = computed(() => readingResultStore.getReadingResultList);
    const setShowNewIcon = computed(() => readingResultStore.getShowNewIcon);
    const showLoadingBox = computed(() => dicomStore.getLoadingState);
    const imageLoading = computed(() => dicomStore.getImageLoadingState);
    const withCAD = computed(() => usersStore.getWithCAD);
    const dicomImageElement = ref<HTMLImageElement | null>(null);
    const analysisImageElement = ref<HTMLImageElement | null>(null);
    const fileLoaded = computed(() => useViewerStore().getFileLoadState);
    const activeDeleteBtn = computed(() => dicomStore.getActiveDeleteBtnState);
    const target = computed(() => useExportStore().getTargetState)
    const format = computed(() => useExportStore().getFormatState)
    const exportImage = computed(() => useExportStore().getExportState)

    const handleDicomTagClick = () => {
      if (dicomFile.value?.unique_filename && !showLoadingBox.value) {
        popupStore.togglePopup('dicomTag', true);
      }
    }

    const handleExportClick = () => {
      if (dicomFile.value?.unique_filename && !showLoadingBox.value) {
        usePopupStore().togglePopup('export', true);
      }
    }
    
    const handlePacsBtnClick = async () => {
      usePopupStore().togglePopup('nowConnect', true);
      const configInfoExist = await usePacsStore().getPacsConfig();
      if(configInfoExist) {
        const today = new Date()
        await usePacsStore().searchPacsData("", "", `${moment(today).format('YYYYMMDD')}-${moment(today).format('YYYYMMDD')}`, 1);
        usePopupStore().togglePopup('nowConnect', false);
        usePopupStore().togglePopup('pacs', true);
      } else {
        usePopupStore().togglePopup('pacsSetting', true);
      }
    }

    const handleRstListClick = () => {
      if(readingResultList.value.length > 0){
        usePopupStore().togglePopup('resultList', true);
        readingResultStore.setShowNewIcon(false);
      }
    }

    const setSelectHeatmapOption = (option: string) => {
      selectHeatmap.value = option
    }

    const debouncedUpdateViewport = debounce(() => {
      updateViewport();
    }, 150);
    
    const debouncedUpdateTubeThreshold = debounce(() => {
      displayTubeMask()
    }, 150)

    const debouncedUpdateHeatmap = debounce(() => {
      displayHeatmap()
    }, 150)

    async function updateViewport() {
      if(!activeDeleteBtn.value) {
        if (!dicomElementLeft.value || !dicomElementRight.value || windowWidth.value === null || windowCenter.value === null) return
        const viewportLeft = cornerstone.getViewport(dicomElementLeft.value)
        const viewportRight = cornerstone.getViewport(dicomElementRight.value)
        viewportLeft.voi.windowWidth = dicomInfo.value.window_width
        viewportLeft.voi.windowCenter = dicomInfo.value.window_center
        viewportRight.voi.windowWidth = dicomInfo.value.window_width
        viewportRight.voi.windowCenter = dicomInfo.value.window_center
        cornerstone.setViewport(dicomElementRight.value, viewportRight)
        cornerstone.updateImage(dicomElementRight.value)
        cornerstone.setViewport(dicomElementLeft.value, viewportLeft)
        cornerstone.updateImage(dicomElementLeft.value)
        if(withCAD.value) {
          await drawProbabilityMap(cachedTubeTensor as tf.Tensor2D, dicomInfo.value.window_width, dicomInfo.value.window_center)
          await displayHeatmap();
        } 
      }
    }

    async function displayDicomImage() {
      const imageId = `wadouri:${baseURL}/dicom/${dicomFile.value.unique_filename}.dcm`;
      image.value = await cornerstone.loadAndCacheImage(imageId);
      const pixelData = image.value.getPixelData()
      let min = pixelData[0]
      let max = pixelData[0]

      if (image.value.minPixelValue !== undefined && image.value.maxPixelValue !== undefined) {
        min = image.value.minPixelValue;
        max = image.value.maxPixelValue;
      } else {
        const pixelData = image.value.getPixelData();
        min = pixelData.reduce((a: any, b: any) => Math.min(a, b));
        max = pixelData.reduce((a: any, b: any) => Math.max(a, b));
      }

      image.value.minPixelValue = min
      image.value.maxPixelValue = max

      const ww = image.value.windowWidth || 0
      const wc = image.value.windowCenter || 0

      windowWidth.value = ww
      windowCenter.value = wc
      initialWindowWidth.value = ww
      initialWindowCenter.value = wc

      const { rows, columns } = image.value
      const leftContainer = dicomElementLeft.value!
      const rightContainer = dicomElementRight.value!

      const viewportLeft = cornerstone.getDefaultViewportForImage(leftContainer, image.value)
      const viewportRight = cornerstone.getDefaultViewportForImage(rightContainer, image.value)

      viewportLeft.voi.windowWidth = ww
      viewportLeft.voi.windowCenter = wc
      viewportRight.voi.windowWidth = ww
      viewportRight.voi.windowCenter = wc

      const viewerContainer = viewerElement.value!
      let singleImageWidth = viewerContainer.clientWidth / 2
      const aspectRatio = rows / columns
      let singleImageHeight = singleImageWidth * aspectRatio

      if (singleImageHeight > viewerContainer.clientHeight) {
        singleImageHeight = viewerContainer.clientHeight
        singleImageWidth = singleImageHeight / aspectRatio
      }

      leftContainer.style.width = `${singleImageWidth}px`
      leftContainer.style.height = `${singleImageHeight}px`
      rightContainer.style.width = `${singleImageWidth}px`
      rightContainer.style.height = `${singleImageHeight}px`

      const scale = singleImageWidth / columns  // columns = 원본 width
      viewportLeft.scale = scale
      viewportRight.scale = scale

      cornerstone.displayImage(leftContainer, image.value, viewportLeft)
      cornerstone.displayImage(rightContainer, image.value, viewportRight)

      cornerstone.resize(leftContainer, true)
      cornerstone.resize(rightContainer, true)

      await new Promise<void>((resolve) => {
        let leftDone = false
        let rightDone = false

        const checkDone = () => {
          if (leftDone && rightDone) {
            leftContainer.removeEventListener('cornerstoneimagerendered', onLeft)
            rightContainer.removeEventListener('cornerstoneimagerendered', onRight)
            resolve()
          }
        }

        const onLeft = () => { leftDone = true; checkDone() }
        const onRight = () => { rightDone = true; checkDone() }

        leftContainer.addEventListener('cornerstoneimagerendered', onLeft)
        rightContainer.addEventListener('cornerstoneimagerendered', onRight)
      })

    }

    // displayDicomImage 후 자동 다운로드
    function setupAutoDownload() {
      const container = dicomElementLeft.value!;
      if (!heatmapCanvas.value || !tubeCanvas.value || !probCanvas.value) {
        console.warn("Overlay canvas not ready");
        return;
      }

      downloadDicomWithOverlay(
        container,
        [probCanvas.value!, heatmapCanvas.value!, tubeCanvas.value!],
        dicomFile.value.unique_filename
      );
    }

    async function downloadDicomWithOverlay(
      dicomElement: HTMLDivElement,
      overlayCanvases: HTMLCanvasElement[], // 예: [heatmapCanvas, tubeCanvas]
      filename: string
    ) {
      if (!image.value) {
        console.error("DICOM image not loaded");
        return;
      }

      const rows = image.value.rows;
      const columns = image.value.columns;

      // 원본 크기로 offscreen canvas 생성
      const offscreen = document.createElement("canvas");
      offscreen.width = columns;
      offscreen.height = rows;
      const ctx = offscreen.getContext("2d")!;
      
      // 원본 픽셀 데이터를 ImageData로 복사
      const imageData = ctx.createImageData(columns, rows);
      const pixelData = image.value.getPixelData(); // 1D grayscale

      const min = image.value.minPixelValue ?? Math.min(...pixelData);
      const max = image.value.maxPixelValue ?? Math.max(...pixelData);

      for (let i = 0; i < rows * columns; i++) {
        let val = pixelData[i];

        // clamp & normalize
        val = Math.max(min, Math.min(max, val));
        const gray = Math.round(((val - min) / (max - min)) * 255);

        const idx = i * 4;
        imageData.data[idx] = gray;
        imageData.data[idx + 1] = gray;
        imageData.data[idx + 2] = gray;
        imageData.data[idx + 3] = 255;
      }
      ctx.putImageData(imageData, 0, 0);

      // prediction overlay 합성 (원본 크기에 맞춰 resize)
      if (target.value === "prediction") {
        overlayCanvases.forEach((ovCanvas) => {
          let shouldDraw = false;
          let alpha = 1.0;

          if (ovCanvas === heatmapCanvas.value && showHeatmapCanvas.value) {
            shouldDraw = true;
            if(showProbCanvas.value) {
              alpha = 0.3;
            }
          }

          if (ovCanvas === probCanvas.value && showProbCanvas.value) {
            shouldDraw = true;
          }

          if (ovCanvas === tubeCanvas.value && showTubeCanvas.value) {
            shouldDraw = true;
          }

          if (shouldDraw) {
            ctx.globalAlpha = alpha;
            ctx.drawImage(ovCanvas, 0, 0, ovCanvas.width, ovCanvas.height, 0, 0, columns, rows);
          }
        });
        ctx.globalAlpha = 1.0;
      }
      
      const dataUrl = offscreen.toDataURL("image/png");

      if(format.value == 'png') {
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = filename+".png";
        link.click();
      } else if(format.value == 'dcm') {
        const blob = dataURLToBlob(dataUrl);
        const url = URL.createObjectURL(blob);
        dicomStore.handleExportFile(target.value, blob);
      }
      useExportStore().setExportState(false);
    }

    function dataURLToBlob(dataUrl: string): Blob {
      const arr = dataUrl.split(',');
      const mimeMatch = arr[0].match(/:(.*?);/);
      if (!mimeMatch) throw new Error('Invalid dataURL');
      const mime = mimeMatch[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new Blob([u8arr], { type: mime });
    }

    const readBinMask = (buffer: ArrayBuffer): tf.Tensor3D => {
      const headerView = new DataView(buffer, 0, 12)
      const c = headerView.getUint32(0, true)
      const h = headerView.getUint32(4, true)
      const w = headerView.getUint32(8, true)
      const data = new Float32Array(buffer).subarray(3) // header 3개 건너뛰기

      return tf.tensor(data, [c, h, w])
    }

    /** Tube Mask 로드 */
    async function loadTubeMask(binBuffer: ArrayBuffer): Promise<void> {
      // 이전 cached tensor가 있으면 먼저 dispose
      if (cachedTubeTensor) {
        cachedTubeTensor.dispose()
        cachedTubeTensor = null
      }

      // 새 tensor 생성
      const tubeTensor = readBinMask(binBuffer)

      // slice & squeeze 후 cachedTubeTensor에 저장
      cachedTubeTensor = tf.tidy(() => {
        return tubeTensor.slice([0, 0, 0], [1, -1, -1]).squeeze() as tf.Tensor2D
      })

      // 원본 tensor는 dispose
      tubeTensor.dispose()
    }

    function displayTubeMask() {
      const tubeTensor = cachedTubeTensor; 
      if (!tubeTensor) return;

      tf.tidy(() => {
        const tubeMask = tubeTensor.greaterEqual(dicomInfo.value.tube_threshold).cast('int32');

        const r = tubeMask.equal(1).mul(255).toFloat();
        const g = tubeMask.equal(3).mul(255).toFloat();
        const b = tubeMask.equal(2).mul(255).toFloat();
        const alpha = tubeMask.greater(0).mul(255).toFloat(); // alpha 항상 포함

        const rgba = tf.stack([r, g, b, alpha], 2) as tf.Tensor3D;

        drawMaskOnCanvas(rgba);
      });
    }

    let tubeOffscreenCanvas: HTMLCanvasElement | null = null;
    async function drawMaskOnCanvas(maskRGBA: tf.Tensor3D) {
      const baseCanvas = dicomElementRight.value?.querySelector('canvas') as HTMLCanvasElement;
      const tube = tubeCanvas.value;
      if (!baseCanvas || !tube || !image.value || !dicomElementRight.value) return;

      const ctx = tube.getContext('2d');
      if (!ctx) return;

      const topLeft = cornerstone.pixelToCanvas(dicomElementRight.value, { x: 0, y: 0 });
      const bottomRight = cornerstone.pixelToCanvas(dicomElementRight.value, {
        x: image.value.width,
        y: image.value.height,
      });

      const enabledElement = cornerstone.getEnabledElement(dicomElementRight.value);
      const cornerstoneCanvas = enabledElement.canvas;

      const resized = !tubeOffscreenCanvas ||
                      tube.width !== cornerstoneCanvas.width ||
                      tube.height !== cornerstoneCanvas.height;

      if (resized) {
        // 이전 화면 snapshot
        const snapshot = document.createElement("canvas");
        snapshot.width = tube.width;
        snapshot.height = tube.height;
        snapshot.getContext("2d")?.drawImage(tubeOffscreenCanvas || tube, 0, 0);

        // offscreen 생성/resize
        tubeOffscreenCanvas = tubeOffscreenCanvas || document.createElement("canvas");
        tubeOffscreenCanvas.width = cornerstoneCanvas.width;
        tubeOffscreenCanvas.height = cornerstoneCanvas.height;

        // snapshot을 offscreen에 맞게 확대
        const offCtx = tubeOffscreenCanvas.getContext("2d")!;
        offCtx.clearRect(0, 0, tubeOffscreenCanvas.width, tubeOffscreenCanvas.height);
        offCtx.drawImage(snapshot, 0, 0, snapshot.width, snapshot.height, 0, 0, tubeOffscreenCanvas.width, tubeOffscreenCanvas.height);

        // 실제 tube canvas 크기 업데이트
        tube.width = cornerstoneCanvas.width;
        tube.height = cornerstoneCanvas.height;
        tube.style.width = `${cornerstoneCanvas.style.width}`;
        tube.style.height = `${cornerstoneCanvas.style.height}`;

        // 기존 화면을 실제 canvas에 바로 보여줌
        ctx.clearRect(0, 0, tube.width, tube.height);
        ctx.drawImage(tubeOffscreenCanvas, 0, 0);
      }

      const drawWidth = Math.round(bottomRight.x - topLeft.x);
      const drawHeight = Math.round(bottomRight.y - topLeft.y);

      // Tensor resize
      const resizedMask = tf.tidy(() => tf.image.resizeBilinear(maskRGBA, [drawHeight, drawWidth], false));
      const maskData = await resizedMask.data();
      resizedMask.dispose();

      if(tubeOffscreenCanvas != null) {
        // offscreen에 tube 그리기
        const offCtx = tubeOffscreenCanvas.getContext("2d")!;
        const imageData = offCtx.createImageData(drawWidth, drawHeight);
        for (let i = 0; i < drawWidth * drawHeight; i++) {
          const idx = i * 4;
          imageData.data[idx]     = Math.round(maskData[idx]);
          imageData.data[idx + 1] = Math.round(maskData[idx + 1]);
          imageData.data[idx + 2] = Math.round(maskData[idx + 2]);
          imageData.data[idx + 3] = Math.round(maskData[idx + 3]);
        }
        offCtx.putImageData(imageData, Math.round(topLeft.x), Math.round(topLeft.y));

        // 실제 canvas에 덮어쓰기
        requestAnimationFrame(() => {
          ctx.clearRect(0, 0, tube.width, tube.height);
          ctx.drawImage(tubeOffscreenCanvas!, 0, 0);
        });
      }
    }

    async function loadHeatmap(completeBinBuffer: ArrayBuffer, incompleteBinBuffer: ArrayBuffer): Promise<void> {
      const completeTensor = readBinMask(completeBinBuffer) // shape: [1, H, W]
      const incompleteTensor = readBinMask(incompleteBinBuffer) // shape: [1, H, W]

      // (1, H, W) → (H, W)
      const completeHeatmap = completeTensor.squeeze() as tf.Tensor2D
      const incompleteHeatmap = incompleteTensor.squeeze() as tf.Tensor2D
      cachedCompleteHeatmapTensor = completeHeatmap.expandDims(2) as tf.Tensor3D
      cachedIncompleteHeatmapTensor = incompleteHeatmap.expandDims(2) as tf.Tensor3D

      completeTensor.dispose()
      incompleteTensor.dispose()
    }

    async function displayHeatmap() {
      if(selectHeatmap.value == 'c') { // Complete
        await drawHeatmapOnCanvas(cachedCompleteHeatmapTensor)
      } else { // Incomplete
        await drawHeatmapOnCanvas(cachedIncompleteHeatmapTensor)
      }
    }

    let heatmapOffscreenCanvas: HTMLCanvasElement | null = null;
    async function drawHeatmapOnCanvas(maskRGBA: tf.Tensor3D | null) {
      if (!maskRGBA || !image.value || !dicomElementRight.value || !heatmapCanvas.value) return;

      const baseCanvas = dicomElementRight.value.querySelector('canvas') as HTMLCanvasElement;
      const heatmapCanvasEl = heatmapCanvas.value;
      const ctx = heatmapCanvasEl.getContext('2d');
      if (!ctx) return;

      const topLeft = cornerstone.pixelToCanvas(dicomElementRight.value, { x: 0, y: 0 });
      const bottomRight = cornerstone.pixelToCanvas(dicomElementRight.value, { x: image.value.width, y: image.value.height });
      const drawWidth = Math.round(bottomRight.x - topLeft.x);
      const drawHeight = Math.round(bottomRight.y - topLeft.y);

      // resize 감지
      const resized = !heatmapOffscreenCanvas ||
                      heatmapCanvasEl.width !== baseCanvas.width ||
                      heatmapCanvasEl.height !== baseCanvas.height;

      if (resized) {
        // 이전 화면 snapshot
        const snapshot = document.createElement("canvas");
        snapshot.width = heatmapCanvasEl.width;
        snapshot.height = heatmapCanvasEl.height;
        snapshot.getContext("2d")?.drawImage(heatmapOffscreenCanvas || heatmapCanvasEl, 0, 0);

        // offscreen 생성/resize
        heatmapOffscreenCanvas = heatmapOffscreenCanvas || document.createElement("canvas");
        heatmapOffscreenCanvas.width = baseCanvas.width;
        heatmapOffscreenCanvas.height = baseCanvas.height;

        // snapshot을 offscreenCanvas에 맞게 확대
        const offCtx = heatmapOffscreenCanvas.getContext("2d")!;
        offCtx.clearRect(0, 0, heatmapOffscreenCanvas.width, heatmapOffscreenCanvas.height);
        offCtx.drawImage(snapshot, 0, 0, snapshot.width, snapshot.height, 0, 0, heatmapOffscreenCanvas.width, heatmapOffscreenCanvas.height);

        // 실제 canvas 크기 업데이트
        heatmapCanvasEl.width = baseCanvas.width;
        heatmapCanvasEl.height = baseCanvas.height;
        heatmapCanvasEl.style.width = `${baseCanvas.style.width}`;
        heatmapCanvasEl.style.height = `${baseCanvas.style.height}`;

        // 기존 화면을 실제 canvas에 바로 보여주기 (깜빡임 최소화)
        ctx.clearRect(0, 0, heatmapCanvasEl.width, heatmapCanvasEl.height);
        ctx.drawImage(heatmapOffscreenCanvas, 0, 0);
      }

      // Tensor resize
      const resizedMask = tf.tidy(() => tf.image.resizeBilinear(maskRGBA, [drawHeight, drawWidth], false));
      const maskArray = await resizedMask.array();
      resizedMask.dispose();

      // DICOM 원본 이미지
      const dicomCtx = baseCanvas.getContext('2d');
      if (!dicomCtx) return;
      const baseImageData = dicomCtx.getImageData(Math.round(topLeft.x), Math.round(topLeft.y), drawWidth, drawHeight);

      const image_weight = dicomInfo.value.image_weight ?? 0.5;
      const imageData = ctx.createImageData(drawWidth, drawHeight);

      for (let i = 0; i < drawHeight; i++) {
        for (let j = 0; j < drawWidth; j++) {
          const idx = (i * drawWidth + j) * 4;
          const val = Math.min(Math.max(maskArray[i][j][0], 0), 1);
          const [r, g, b] = applyJetColormap(val);

          const baseR = baseImageData.data[idx];
          const baseG = baseImageData.data[idx + 1];
          const baseB = baseImageData.data[idx + 2];

          imageData.data[idx]     = Math.round((1 - image_weight) * r + image_weight * baseR);
          imageData.data[idx + 1] = Math.round((1 - image_weight) * g + image_weight * baseG);
          imageData.data[idx + 2] = Math.round((1 - image_weight) * b + image_weight * baseB);
          imageData.data[idx + 3] = 255;
        }
      }

      if(heatmapOffscreenCanvas != null) {
        const offCtx = heatmapOffscreenCanvas.getContext("2d")!;
        offCtx.putImageData(imageData, Math.round(topLeft.x), Math.round(topLeft.y));
  
        requestAnimationFrame(() => {
          ctx.clearRect(0, 0, heatmapCanvasEl.width, heatmapCanvasEl.height);
          ctx.drawImage(heatmapOffscreenCanvas!, 0, 0);
        });
      }
    }

    function applyJetColormap(value: number): [number, number, number] {
      const fourValue = 4 * value
      const r = Math.min(fourValue - 1.5, -fourValue + 4.5)
      const g = Math.min(fourValue - 0.5, -fourValue + 3.5)
      const b = Math.min(fourValue + 0.5, -fourValue + 2.5)
      return [
        Math.max(0, Math.min(1, r)) * 255,
        Math.max(0, Math.min(1, g)) * 255,
        Math.max(0, Math.min(1, b)) * 255
      ]
    }

    let offscreenCanvas: HTMLCanvasElement | null = null;
    async function drawProbabilityMap(probTensor: tf.Tensor2D, ww: number, wc: number) {
      if (!image.value || !dicomElementRight.value || !probCanvas.value) return;

      const baseCanvas = dicomElementRight.value.querySelector("canvas") as HTMLCanvasElement;
      const probCanvasEl = probCanvas.value;
      const ctx = probCanvasEl.getContext("2d");
      if (!ctx || !baseCanvas) return;

      const topLeft = cornerstone.pixelToCanvas(dicomElementRight.value, { x: 0, y: 0 });
      const bottomRight = cornerstone.pixelToCanvas(dicomElementRight.value, {
        x: image.value.width,
        y: image.value.height,
      });

      const maskContainer = maskElement.value!;

      // resize 체크
      const resized = !offscreenCanvas ||
        offscreenCanvas.width !== baseCanvas.width ||
        offscreenCanvas.height !== baseCanvas.height;

      if (resized) {
        // 기존 화면 snapshot
        const snapshot = document.createElement("canvas");
        snapshot.width = probCanvasEl.width;
        snapshot.height = probCanvasEl.height;
        snapshot.getContext("2d")?.drawImage(probCanvasEl, 0, 0);

        // 실제 probCanvas 크기 업데이트
        probCanvasEl.width = baseCanvas.width;
        probCanvasEl.height = baseCanvas.height;
        probCanvasEl.style.width = `${baseCanvas.style.width}`;
        probCanvasEl.style.height = `${baseCanvas.style.height}`;
        maskContainer.style.width = `${baseCanvas.style.width}`;
        maskContainer.style.height = `${baseCanvas.style.height}`;

        // 새 offscreenCanvas 생성
        offscreenCanvas = document.createElement("canvas");
        offscreenCanvas.width = baseCanvas.width;
        offscreenCanvas.height = baseCanvas.height;

        // snapshot을 새 캔버스 크기에 맞게 리사이즈해서 보여줌
        ctx.clearRect(0, 0, probCanvasEl.width, probCanvasEl.height);
        ctx.drawImage(snapshot, 0, 0, snapshot.width, snapshot.height, 0, 0, probCanvasEl.width, probCanvasEl.height);
      }

      const drawWidth = Math.round(bottomRight.x - topLeft.x);
      const drawHeight = Math.round(bottomRight.y - topLeft.y);

      // Tensor resize
      const resizedTensor = tf.tidy(() => {
        const expanded = probTensor.expandDims(-1) as tf.Tensor3D; // [H, W, 1]
        const resized = tf.image.resizeBilinear(expanded, [drawHeight, drawWidth], false);
        expanded.dispose();
        return resized.squeeze(); // [H, W]
      });

      const arr2D = (await resizedTensor.array()) as number[][];
      resizedTensor.dispose();

      // Viridis colormap 적용 + window center/width 반영
      const imageData = await applyViridisAndCombineResized(
        image.value,
        arr2D,
        Number(ww),
        Number(wc),
        drawWidth,
        drawHeight,
        0.9 // alpha
      );
      if (!imageData) return;

      const offCtx = offscreenCanvas!.getContext("2d")!;
      offCtx.clearRect(0, 0, offscreenCanvas!.width, offscreenCanvas!.height);
      offCtx.putImageData(imageData, Math.round(topLeft.x), Math.round(topLeft.y));

      requestAnimationFrame(() => {
        ctx.clearRect(0, 0, probCanvasEl.width, probCanvasEl.height);
        ctx.drawImage(offscreenCanvas!, 0, 0);
      });
    }

    function applyViridisColormap(value: number): [number, number, number] {
      const idx = Math.min(255, Math.max(0, Math.floor(value * 255)));
      const rgb = viridisLUT[idx];
      return [rgb.r, rgb.g, rgb.b];
    }

    async function applyViridisAndCombineResized(
      image: any,
      probArray2D: number[][],
      windowWidth: number,
      windowCenter: number,
      targetWidth: number,
      targetHeight: number,
      alpha = 0.9
    ): Promise<ImageData | null> {
      if (!image || !probArray2D) return null;

      const width = image.width;
      const height = image.height;
      const pixelData1D = image.getPixelData();
      const floatArray = Float32Array.from(pixelData1D);

      const grayTensor = tf.tidy(() => tf.tensor(floatArray, [height, width, 1]));
      const resizedGray = tf.tidy(() => tf.image.resizeBilinear(grayTensor as tf.Tensor3D, [targetHeight, targetWidth]));
      const grayData = resizedGray.dataSync();
      grayTensor.dispose();
      resizedGray.dispose();

      const imageData = new ImageData(targetWidth, targetHeight);
      const data = imageData.data;

      for (let i = 0; i < targetHeight; i++) {
        for (let j = 0; j < targetWidth; j++) {
          const idx = i * targetWidth + j;
          const rawVal = grayData[idx];
          const probVal = probArray2D[i][j] ?? 0;

          const normVal = applyWWWC(rawVal, windowWidth, windowCenter);
          const [r, g, b] = applyViridisColormap(normVal);
          const gray = Math.round(probVal * 255);

          data[idx * 4] = Math.round(r * alpha + gray * (1 - alpha));
          data[idx * 4 + 1] = Math.round(g * alpha + gray * (1 - alpha));
          data[idx * 4 + 2] = Math.round(b * alpha + gray * (1 - alpha));
          data[idx * 4 + 3] = 255;
        }
      }

      return imageData;
    }

    function applyWWWC(value: number, ww: number, wc: number): number {
      if (ww < 1) ww = 1;
      const minVal = wc - ww / 2;
      const maxVal = wc + ww / 2;
      const normalized = (value - minVal) / (maxVal - minVal);

      return Math.max(0, Math.min(1, normalized));
    }

    const handleResize = async () => {
      if (!image.value || !dicomElementLeft.value || !dicomElementRight.value) return

      await nextTick()

      const leftContainer = dicomElementLeft.value!
      const rightContainer = dicomElementRight.value!
      const maskContainer = maskElement.value!
      const viewerContainer = viewerElement.value!
      const { rows, columns } = image.value

      const aspectRatio = rows / columns
      let singleImageWidth = viewerContainer.clientWidth / 2
      let singleImageHeight = singleImageWidth * aspectRatio

      if (singleImageHeight > viewerContainer.clientHeight) {
        singleImageHeight = viewerContainer.clientHeight
        singleImageWidth = singleImageHeight / aspectRatio
      }

      const setContainerSize = (container: HTMLElement) => {
        container.style.width = `${singleImageWidth}px`
        container.style.height = `${singleImageHeight}px`
      }

      setContainerSize(leftContainer)
      setContainerSize(rightContainer)
      setContainerSize(maskContainer)

      const scale = singleImageWidth / columns
      const viewportLeft = cornerstone.getViewport(leftContainer)!
      const viewportRight = cornerstone.getViewport(rightContainer)!

      viewportLeft.scale = scale
      viewportRight.scale = scale

      cornerstone.setViewport(leftContainer, viewportLeft)
      cornerstone.setViewport(rightContainer, viewportRight)

      cornerstone.resize(leftContainer, true)
      cornerstone.resize(rightContainer, true)
    }

    const createImageElement = (src: string, signal: AbortSignal): Promise<HTMLImageElement> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.classList.add("result_img", "visible");

        // 🔥 abort 시 이미지 로딩 중단
        const abortHandler = () => {
          img.src = ""; // 메모리 해제
          reject(new DOMException("Aborted", "AbortError"));
        };

        // 로드 성공
        img.onload = () => {
          signal.removeEventListener("abort", abortHandler);
          resolve(img);
        };

        // 로드 실패
        img.onerror = (err) => {
          signal.removeEventListener("abort", abortHandler);
          reject(err);
        };

        // AbortSignal 감지
        signal.addEventListener("abort", abortHandler);

        // 가장 마지막에 src 설정
        img.src = src;
      });
    };


    watch(
      () => dicomImg.value,
      async (newVal, oldVal) => {
        await displayDicomImage();

        if (withCAD.value) {
          loadTubeMask(buffers.value['tube']);
          displayTubeMask();

          loadHeatmap(buffers.value['completeCam'], buffers.value['incompleteCam']);
          displayHeatmap();

          await drawProbabilityMap(
            cachedTubeTensor as tf.Tensor2D,
            windowWidth.value as number,
            windowCenter.value as number
          );
        }
      }
    );

    let currentController: AbortController | null = null;
    watch(
      () => dicomFile.value,
      async (newVal) => {
        if (!newVal || Object.keys(newVal).length === 0) return;

        dicomImageElement.value = null;
        analysisImageElement.value = null;

        const filename = newVal.unique_filename;
        const urls = [`${baseURL}/dicom_png/${filename}.png`];

        if (withCAD.value) {
          urls.push(`${baseURL}/analysis_png/${filename}.png`);
        }

        // 🔥 이전 요청 취소
        if (currentController) {
          currentController.abort();
        }
        currentController = new AbortController();
        const signal = currentController.signal;

        try {
          const images = await Promise.all(
            urls.map((url) => createImageElement(url, signal))
          );

          // 취소된 요청이라면 UI 반영 X
          if (signal.aborted) return;

          dicomImageElement.value = images[0];
          if (withCAD.value) analysisImageElement.value = images[1];
          dicomStore.setImageLoading(false);
          useTimerStore().startTimer();
        } catch (err: any) {
          if (err.name !== "AbortError") {
            console.error("이미지 로딩 실패", err);
          }
        }
      }
    );


    watch(
      () => dicomInfo.value.window_center,
      (newVal) => {
        // if(fileLoaded.value) {
          if(withCAD.value) {
            debouncedUpdateViewport()
          } else {
            updateViewport()
          }
        // }
      }
    );
    
    watch(
      () => dicomInfo.value.window_width,
      (newVal) => {
        // if(fileLoaded.value) {
          if(withCAD.value) {
            debouncedUpdateViewport()
          } else {
            updateViewport()
          }
        // }
      }
    );
    
    watch(
      () => dicomInfo.value.tube_threshold,
      (newVal) => {
        // if(fileLoaded.value) {
          debouncedUpdateTubeThreshold()
        // }
      }
    );
    
    watch(
      () => dicomInfo.value.select_heatmap_idx,
      (newVal) => {
        selectHeatmap.value = newVal === 0 ? 'c' : 'i'
        // if(fileLoaded.value) {
          debouncedUpdateHeatmap()
        // }
      }
    );
    
    watch(
      () => dicomInfo.value.image_weight,
      (newVal) => {
        // if(fileLoaded.value) {
          debouncedUpdateHeatmap()
        // }
      }
    );

    watch(exportImage, (newVal) => {
      if(newVal) {
        setupAutoDownload();
      }
    })

    watch(activeDeleteBtn, (newVal) => {
      if(newVal) {
        dicomImageElement.value = null;
        analysisImageElement.value = null;
      }
    })

    onMounted(async () => {
      await nextTick()
      if (viewerElement.value) {
        resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            if (entry.target === viewerElement.value) {
              viewerWidth.value = entry.contentRect.width
              handleResize()
            }
          }
        })
        resizeObserver.observe(viewerElement.value)
      }

      window.addEventListener('resize', handleResize)

      if (!dicomElementRight.value) return

      cornerstone.enable(dicomElementLeft.value)
      cornerstone.enable(dicomElementRight.value)
    })

    onBeforeUnmount(() => {
      if (resizeObserver && viewerElement.value) {
        resizeObserver.unobserve(viewerElement.value)
      }
      window.removeEventListener('resize', handleResize)
      dicomImageElement.value = null;
      analysisImageElement.value = null;
      useReadingResultStore().initReadingResultList();
      readingResultStore.setShowNewIcon(false);
    })

    return {
      dicomElementLeft,
      dicomElementRight,
      maskElement,
      viewerElement,
      windowWidth,
      windowCenter,
      initialWindowWidth,
      initialWindowCenter,
      updateViewport,
      tubeCanvas,
      heatmapCanvas,
      probCanvas,
      tubeThreshold,
      selectHeatmap,
      setSelectHeatmapOption,
      showTubeCanvas,
      showHeatmapCanvas,
      showProbCanvas,
      imageWeight,
      drawHeatmapOnCanvas,
      debouncedUpdateViewport,
      debouncedUpdateTubeThreshold,
      debouncedUpdateHeatmap,
      dicomInfo,
      dicomFile,
      readingResultList,
      setShowNewIcon,
      handleDicomTagClick,
      withCAD,
      handleExportClick,
      showLoadingBox,
      imageLoading,
      handlePacsBtnClick,
      handleRstListClick,
      dicomImageElement,
      analysisImageElement,
      fileLoaded,
      activeDeleteBtn
    }
  }
})
